import { cn } from '@/lib/utils';
import type { FreshnessStatus } from '@/lib/freshness';

/**
 * Uniform freshness badge (replit.md §0.7 + L8/UI_STANDARDS). Every data view
 * uses THIS component (not its own) so freshness looks identical everywhere.
 * Accessibility: never colour alone — a status dot is paired with an Estonian
 * text label, and the badge is announced via role="status".
 *
 * NOTE (L12 — Canvas before code): the visual treatment here uses functional
 * default colours; final token alignment + Canvas approval land with the
 * design-foundation module (0.9). The status→meaning mapping is stable.
 */
const STATUS_CONFIG: Record<FreshnessStatus, { label: string; dot: string; text: string }> = {
  green: { label: 'Värske', dot: 'bg-emerald-500', text: 'text-emerald-300' },
  amber: { label: 'Vananev', dot: 'bg-amber-500', text: 'text-amber-300' },
  red: { label: 'Aegunud', dot: 'bg-red-500', text: 'text-red-300' },
};

export interface FreshnessBadgeProps {
  status: FreshnessStatus;
  /** Override the default Estonian label (e.g. to append an age). */
  label?: string;
  className?: string;
}

export function FreshnessBadge({ status, label, className }: FreshnessBadgeProps) {
  const config = STATUS_CONFIG[status];
  return (
    <span
      role="status"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-xs font-medium',
        config.text,
        className,
      )}
    >
      <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', config.dot)} />
      {label ?? config.label}
    </span>
  );
}
